// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Small SVG helpers shared by the hand-rolled charts. No chart library: every
// view here needs bespoke interaction, and a library would be fought rather
// than used.

const NS = 'http://www.w3.org/2000/svg';

export function svgEl<K extends keyof SVGElementTagNameMap>(
  name: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, name);
  for (const [k, val] of Object.entries(attrs)) el.setAttribute(k, String(val));
  return el;
}

/**
 * Attach a hover tooltip to a data mark.
 *
 * `setAttribute` takes RAW newlines — the tooltip's `white-space: pre-line`
 * turns them into line breaks. Passing "&#10;" here renders those five
 * characters literally, which is only correct when building an HTML string.
 *
 * A native SVG <title> is deliberately NOT used: it is slow to appear,
 * unstyleable, and invisible on touch.
 */
export function markTip(el: Element, text: string, ariaLabel?: string): void {
  el.setAttribute('data-tip', text);
  if (ariaLabel !== undefined) el.setAttribute('aria-label', ariaLabel);
}

export interface Scale {
  (v: number): number;
  invert(px: number): number;
  domain: [number, number];
  range: [number, number];
}

export function linear(domain: [number, number], range: [number, number]): Scale {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0 || 1;
  const fn = ((v: number) => r0 + ((v - d0) / span) * (r1 - r0)) as Scale;
  fn.invert = (px: number) => d0 + ((px - r0) / (r1 - r0 || 1)) * span;
  fn.domain = domain;
  fn.range = range;
  return fn;
}

/** Round numbers for an axis: 0, 250, 500… rather than 0, 237, 474… */
export function niceTicks(max: number, count = 5): number[] {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  const step = (norm >= 7.5 ? 10 : norm >= 3.5 ? 5 : norm >= 1.5 ? 2 : 1) * mag;
  const out: number[] = [];
  for (let t = 0; t <= max + step * 0.001; t += step) out.push(Math.round(t * 1e6) / 1e6);
  return out;
}

/** A chart shell: <figure> with an accessible name, an optional scroller, and a zoom anchor. */
export function chartFrame(opts: {
  title: string;
  subtitle?: string;
  scroll?: boolean;
  minWidth?: number;
}): { figure: HTMLElement; mount: HTMLElement } {
  const figure = document.createElement('figure');
  figure.className = 'card';
  figure.style.margin = '0';

  const cap = document.createElement('figcaption');
  cap.innerHTML = `<div class="card-title">${opts.title}</div>${opts.subtitle ? `<div class="card-sub">${opts.subtitle}</div>` : ''}`;
  cap.style.marginBottom = 'var(--space-md)';
  figure.append(cap);

  const frame = document.createElement('div');
  frame.className = 'chart-frame';
  if (opts.scroll) {
    const scroller = document.createElement('div');
    scroller.className = 'chart-scroll';
    if (opts.minWidth) scroller.style.setProperty('--min-w', `${opts.minWidth}px`);
    frame.append(scroller);
    figure.append(frame);
    return { figure, mount: scroller };
  }
  figure.append(frame);
  return { figure, mount: frame };
}

/**
 * Charts are `role="group"`, never `role="img"`.
 *
 * `role="img"` makes the element an accessibility LEAF: every focusable mark
 * inside it is pruned from the tree while remaining in the tab order, so a
 * keyboard user tabs into things a screen reader cannot name.
 */
export function labelChart(svg: SVGSVGElement, name: string, description?: string): void {
  svg.setAttribute('role', 'group');
  svg.setAttribute('aria-label', description ? `${name}. ${description}` : name);
  svg.classList.add('chart');
}
